import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { addReportToServer } from "../../functions/sendReport";
import { saveResult } from "../../services/saveResult";
import "./MemoryGamePage.css";

const GRID_SIZE = 5;
const MAX_LEVEL = 10;
const DEMO_MAX_LEVEL = 2;
const REVEAL_MS = 1500;
const LOCK_MS = 200;
const LEVEL_START_DELAY_MS = 1500;
const LEVEL_CLEAR_GLITTER_MS = 1000;
const LEVEL_CLEAR_GLITTER_SPARKS = 24;
const LEVEL_PATTERN_LENGTH = {
  1: 3,
  2: 5,
  3: 6,
  4: 7,
  5: 8,
  6: 9,
  7: 10,
  8: 11,
  9: 12,
  10: 13,
};
const LEVEL_BADGES = {
  3: { title: "Beginner Mind", image: "/img/level3_medal.png" },
  5: { title: "Pattern Player", image: "/img/level5_medal.png" },
  7: { title: "Memory Pro", image: "/img/level7_medal.png" },
  10: { title: "Recall Expert", image: "/img/level10_medal.png" },
};

const FLAWLESS_BADGE = {
  title: "Flawless Run",
  image: "/img/complete_all_stages_medal.png",
};

function gameRunStorageKey(organizationId, sessionId, userId) {
  return `memory-game-run:${organizationId}:${sessionId}:${userId || "guest"}`;
}

function readGameRunNumber(storageKey) {
  try {
    const parsed = Number(sessionStorage.getItem(storageKey));
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
  } catch {
    return 1;
  }
}

function bumpGameRunNumber(storageKey) {
  const next = readGameRunNumber(storageKey) + 1;
  try {
    sessionStorage.setItem(storageKey, String(next));
  } catch {
    /* ignore */
  }
  return next;
}

function readEarnedBadgeLevels(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0));
  } catch {
    return new Set();
  }
}

function markEarnedBadgeLevel(storageKey, badgeLevel) {
  const earned = readEarnedBadgeLevels(storageKey);
  earned.add(badgeLevel);
  localStorage.setItem(storageKey, JSON.stringify([...earned]));
}

function hasEarnedFlawless(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

function markFlawlessEarned(storageKey) {
  try {
    localStorage.setItem(storageKey, "1");
  } catch {
    /* ignore */
  }
}

async function awardBadgeForClearedLevel(clearedLevel, earnedBadgesStorageKey) {
  const badge = LEVEL_BADGES[clearedLevel];
  if (!badge) return;
  const earned = readEarnedBadgeLevels(earnedBadgesStorageKey);
  if (earned.has(clearedLevel)) return;
  await showBadgePopup(badge);
  markEarnedBadgeLevel(earnedBadgesStorageKey, clearedLevel);
}

async function awardFlawlessBadge(flawlessStorageKey) {
  if (hasEarnedFlawless(flawlessStorageKey)) return;
  await showBadgePopup(FLAWLESS_BADGE);
  markFlawlessEarned(flawlessStorageKey);
}

function showBadgePopup(badge) {
  if (!badge?.image) return Promise.resolve();
  return Swal.fire({
    title: "Congratulations, you have earned a badge!",
    imageUrl: badge.image,
    imageAlt: badge.title || "Medal",
    imageWidth: 220,
    showCloseButton: false,
    text: "",
    confirmButtonText: "OK",
    customClass: {
      popup: "memory-badge-popup",
      title: "memory-badge-popup__text",
      image: "memory-badge-popup__image",
      confirmButton: "memory-badge-popup__ok",
    },
    buttonsStyling: false,
  });
}

function pickUniqueCells(count) {
  const max = GRID_SIZE * GRID_SIZE;
  const set = new Set();
  while (set.size < count) {
    set.add(Math.floor(Math.random() * max));
  }
  return Array.from(set);
}

function perTileRevealMs(levelValue) {
  if (levelValue <= 3) return 600;
  if (levelValue <= 7) return 500;
  return 400;
}

function appBaseUrl() {
  const base = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return base || window.location.origin;
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

function clampLevel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(MAX_LEVEL, Math.max(1, Math.floor(numeric)));
}

export default function MemoryGamePage() {
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const [level, setLevel] = useState(1);
  const [target, setTarget] = useState([]);
  const [selected, setSelected] = useState([]);
  const [phase, setPhase] = useState("reveal"); // reveal | play | checking
  const [startedAt] = useState(() => Date.now());
  const [madeMistake, setMadeMistake] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  const [rulesOpen, setRulesOpen] = useState(false);
  const [showLevelClearGlitter, setShowLevelClearGlitter] = useState(false);
  const revealTimerRef = useRef(null);
  const lockTimerRef = useRef(null);
  const glitterTimerRef = useRef(null);
  const hasBootstrappedLevelRef = useRef(false);
  const touchedRef = useRef(false);
  const reportIdRef = useRef(user?.reportId ?? null);

  const userId = user?.userId || "";
  const sessionId = user?.sessionId || "remember-memory";
  const organizationId = user?.organizationId || "default-org";
  const progressStorageKey = `memory-progress:${organizationId}:${sessionId}:${userId || "guest"}`;
  const bestLevelStorageKey = `memory-best-level:${organizationId}:${sessionId}:${userId || "guest"}`;
  const earnedBadgesStorageKey = `memory-earned-badges:${organizationId}:${sessionId}:${userId || "guest"}`;
  const flawlessBadgeStorageKey = `memory-earned-flawless:${organizationId}:${sessionId}:${userId || "guest"}`;
  const gameRunKey = gameRunStorageKey(organizationId, sessionId, userId);
  const gameRunNumberRef = useRef(readGameRunNumber(gameRunKey));

  const progressPct = Math.round((level / MAX_LEVEL) * 100);
  const pageBgImage = `${process.env.PUBLIC_URL || ""}${isMobile ? "/img/mobile.jpg" : "/img/desktop.jpg"}`;
  const grid = useMemo(() => Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, i) => i), []);
  const pushBestLevelReport = (bestLevelValue) => {
    if (!user) return;
    const elapsedSec = Math.max(1, Math.floor((Date.now() - startedAt) / 1000));
    addReportToServer(user, {
      points: Number(bestLevelValue) || 0,
      elapsedSeconds: elapsedSec,
      answers: [`best_level:${bestLevelValue}`],
      reportIdRef,
    });
  };

  const saveLevelProgress = async (levelValue, { flawlessRun = false, singleAttempt } = {}) => {
    const elapsedSec = Math.max(1, Math.floor((Date.now() - startedAt) / 1000));
    const isSingleAttempt = singleAttempt ?? gameRunNumberRef.current === 1;
    const result = await saveResult({
      userId: userId || `guest_${Date.now()}`,
      sessionId,
      organizationId,
      email: user?.email || "",
      name: user?.name || "",
      points: levelValue,
      totalQuestions: MAX_LEVEL,
      attemptedQuestions: levelValue,
      timeLeft: elapsedSec,
      percentage: Math.round((levelValue / MAX_LEVEL) * 100),
      flawlessRun: Boolean(flawlessRun),
      singleAttempt: isSingleAttempt,
    }).catch((err) => {
      console.error("save-result failed:", err);
    });
    if (!result?.success) return;
    const saved = result.data || {};
    const savedBestLevel = Number(saved.bestLevel);
    const shouldReport =
      saved.isNewBest === true
      || saved.isNewRow === true
      || saved.bestTimeRepaired === true;

    if (Number.isFinite(savedBestLevel)) {
      localStorage.setItem(bestLevelStorageKey, String(savedBestLevel));
    }

    if (shouldReport) {
      pushBestLevelReport(Number.isFinite(savedBestLevel) ? savedBestLevel : levelValue);
    }
  };

  const startLevel = (nextLevel) => {
    const revealCount = LEVEL_PATTERN_LENGTH[nextLevel] || 3;
    setTarget(pickUniqueCells(revealCount));
    setSelected([]);
    setPhase("reveal");
  };

  useEffect(() => {
    if (hasBootstrappedLevelRef.current) return;
    hasBootstrappedLevelRef.current = true;
    const restoredLevel = clampLevel(localStorage.getItem(progressStorageKey));
    setLevel(restoredLevel);
    startLevel(restoredLevel);
  }, [progressStorageKey]);

  useEffect(() => {
    localStorage.setItem(progressStorageKey, String(clampLevel(level)));
  }, [progressStorageKey, level]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const previousStyles = {
      backgroundImage: document.body.style.backgroundImage,
      backgroundSize: document.body.style.backgroundSize,
      backgroundRepeat: document.body.style.backgroundRepeat,
      backgroundPosition: document.body.style.backgroundPosition,
      backgroundAttachment: document.body.style.backgroundAttachment,
    };

    document.body.style.backgroundImage = `url(${pageBgImage})`;
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundRepeat = "no-repeat";
    document.body.style.backgroundPosition = "center top";
    document.body.style.backgroundAttachment = "fixed";

    return () => {
      document.body.style.backgroundImage = previousStyles.backgroundImage;
      document.body.style.backgroundSize = previousStyles.backgroundSize;
      document.body.style.backgroundRepeat = previousStyles.backgroundRepeat;
      document.body.style.backgroundPosition = previousStyles.backgroundPosition;
      document.body.style.backgroundAttachment = previousStyles.backgroundAttachment;
    };
  }, [pageBgImage]);

  useEffect(() => {
    if (phase !== "reveal") return undefined;
    const revealCount = LEVEL_PATTERN_LENGTH[level] || 3;
    const revealDuration = revealCount * perTileRevealMs(level);
    revealTimerRef.current = window.setTimeout(() => {
      setPhase("play");
    }, revealDuration);
    return () => {
      if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current);
    };
  }, [phase, level, target.length]);

  useEffect(() => {
    if (!userId || touchedRef.current) return;
    touchedRef.current = true;
    saveResult({
      userId,
      sessionId,
      organizationId,
      email: user?.email || "",
      name: user?.name || "",
      points: 0,
      totalQuestions: MAX_LEVEL,
      attemptedQuestions: 0,
      timeLeft: 0,
      percentage: 0,
      touchOnLanding: true,
    }).catch(() => {});
  }, [userId, sessionId, organizationId, user?.email, user?.name]);

  useEffect(() => {
    if (user?.reportId) {
      reportIdRef.current = user.reportId;
    }
  }, [user?.reportId]);

  const finishGame = async () => {
    localStorage.removeItem(progressStorageKey);
    const runNumber = gameRunNumberRef.current;
    const flawlessRun = !madeMistake && runNumber === 1;
    await saveLevelProgress(MAX_LEVEL, {
      flawlessRun,
      singleAttempt: runNumber === 1,
    });
    if (!madeMistake) {
      await awardBadgeForClearedLevel(MAX_LEVEL, earnedBadgesStorageKey);
      if (flawlessRun) {
        await awardFlawlessBadge(flawlessBadgeStorageKey);
      }
    }
    bumpGameRunNumber(gameRunKey);
    navigate("/leaderboard", { replace: true });
  };

  const triggerLevelClearGlitter = () => {
    setShowLevelClearGlitter(false);
    if (glitterTimerRef.current) window.clearTimeout(glitterTimerRef.current);
    const restartGlitter = () => setShowLevelClearGlitter(true);
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(restartGlitter);
    } else {
      window.setTimeout(restartGlitter, 0);
    }
    glitterTimerRef.current = window.setTimeout(() => {
      setShowLevelClearGlitter(false);
    }, LEVEL_CLEAR_GLITTER_MS);
  };

  const onCardClick = (idx) => {
    if (phase !== "play") return;
    if (selected.includes(idx)) return;
    const next = [...selected, idx];
    setSelected(next);
    if (!target.includes(idx)) {
      const failedAtLevel = level;
      setMadeMistake(true);
      setPhase("checking");
      lockTimerRef.current = window.setTimeout(async () => {
        localStorage.removeItem(progressStorageKey);
        const lastCompletedLevel = Math.max(0, failedAtLevel - 1);
        await saveLevelProgress(lastCompletedLevel, { singleAttempt: gameRunNumberRef.current === 1 });
        bumpGameRunNumber(gameRunKey);
        navigate("/leaderboard", { replace: true });
      }, LOCK_MS);
      return;
    }

    if (next.length === target.length) {
      triggerLevelClearGlitter();
      if (level >= MAX_LEVEL) {
        setPhase("checking");
        lockTimerRef.current = window.setTimeout(() => {
          finishGame();
        }, LOCK_MS);
      } else {
        const clearedLevel = level;
        setPhase("checking");
        lockTimerRef.current = window.setTimeout(async () => {
          await saveLevelProgress(clearedLevel);
          await awardBadgeForClearedLevel(clearedLevel, earnedBadgesStorageKey);
          if (user?.source === "DEMO" && clearedLevel >= DEMO_MAX_LEVEL) {
            localStorage.removeItem(progressStorageKey);
            const msg = "Thanks for playing (demo).";
            sessionStorage.setItem("spotThankYouMsg", msg);
            await Swal.fire({
              icon: "success",
              title: "Thank you for playing.",
              text: "Subscribe to any PLAN to play with your peers.",
            });
            window.location.href = demoPlansUrl();
            return;
          }
          const nextLevel = clearedLevel + 1;
          setLevel(nextLevel);
          window.setTimeout(() => {
            startLevel(nextLevel);
          }, LEVEL_START_DELAY_MS);
        }, LOCK_MS);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current);
      if (lockTimerRef.current) window.clearTimeout(lockTimerRef.current);
      if (glitterTimerRef.current) window.clearTimeout(glitterTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!rulesOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setRulesOpen(false);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [rulesOpen]);

  return (
    <main className="memory-page">
      <button
        type="button"
        className="memory-rules-trigger"
        aria-label="Open rules"
        aria-haspopup="dialog"
        aria-expanded={rulesOpen}
        onClick={() => setRulesOpen(true)}
      >
        <img src="/img/rules-img.png" alt="" aria-hidden="true" />
      </button>

      <section className="memory-shell">
        <div className="memory-level-track">
          <div className="memory-level-pill">{level}</div>
          <div className={`memory-progress${showLevelClearGlitter ? " is-celebrating" : ""}`}>
            <div
              className={`memory-progress-fill${showLevelClearGlitter ? " is-celebrating" : ""}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="memory-grid" role="grid" aria-label="Memory board">
          {grid.map((idx) => {
            const isFlipped = (phase === "reveal" && target.includes(idx)) || selected.includes(idx);
            const showColored = phase === "reveal" ? target.includes(idx) : selected.includes(idx) && target.includes(idx);
            const frontSrc = showColored ? "/img/color_star.png" : "/img/white_start.png";
            return (
              <button
                key={idx}
                type="button"
                className={`memory-card${isFlipped ? " is-flipped" : ""}`}
                onClick={() => onCardClick(idx)}
                disabled={phase !== "play"}
                aria-label={`Card ${idx + 1}`}
              >
                <span className="memory-card-inner">
                  <img src="/img/white_start.png" alt="" className="memory-face memory-face--back" />
                  <img src={frontSrc} alt="" className="memory-face memory-face--front" />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {showLevelClearGlitter ? (
        <div className="memory-level-clear-glitter" aria-hidden="true">
          {Array.from({ length: LEVEL_CLEAR_GLITTER_SPARKS }, (_, idx) => (
            <span key={idx} className="memory-level-clear-glitter__spark" />
          ))}
        </div>
      ) : null}

      {rulesOpen ? (
        <div
          className="memory-rules-overlay"
          role="presentation"
          onClick={() => setRulesOpen(false)}
        >
          <div
            className="memory-rules-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="memory-rules-title"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(30, 43, 108, 0.86), rgba(14, 21, 64, 0.88)), url(${pageBgImage})`,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="memory-rules-close"
              aria-label="Close rules"
              onClick={() => setRulesOpen(false)}
            >
              ×
            </button>
            <h2 id="memory-rules-title">How to play</h2>
            <ul>
              <li>Watch the pattern carefully as tiles light up on the grid</li>
              <li>Memorize the exact order of the highlighted tiles</li>
              <li>Patterns become longer and faster as levels increase</li>
              <li>One wrong tap ends the run and sends you back to Level 1</li>
              <li>Complete all 10 levels in one run to prove your memory skills</li>
            </ul>
          </div>
        </div>
      ) : null}
    </main>
  );
}
